import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { enabledModules } from "@/components/AppShell";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { CategoryPicker, CategoryChip } from "@/components/husholdning/CategoryPicker";
import { CATEGORY_COLORS } from "@/lib/constants";
import { HOUSEHOLD_HOME, planTerms } from "@/lib/householdConstants";
import { formatKr } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Tags, BookOpenText, Link2 } from "lucide-react";
import { cn } from "@/lib/utils";

/** Regninger → Kategorier. Når Regnskap-modulen er på, vises og redigeres også koblingen til virksomhetens kontoplan per kategori. */
export default function CategoriesPage() {
  const { canWrite, isOwner, activeWorkspace } = useAuth();
  const [data, setData] = useState(null);
  const [plan, setPlan] = useState(null); // /household/plan (null = ikke lastet / modul av)
  const [dialog, setDialog] = useState(null); // {id?, name, color, household_category_id}
  const [del, setDel] = useState(null);
  const [saving, setSaving] = useState(false);
  const householdOn = !isOwner || enabledModules(activeWorkspace).includes("husholdning");

  const load = () => api.get("/categories").then((r) => setData(r.data)).catch((e) => toast.error(errorMessage(e)));
  const loadPlan = () => (householdOn ? api.get("/household/plan").then((r) => setPlan(r.data)).catch(() => setPlan(null)) : Promise.resolve());
  useEffect(() => { load(); loadPlan(); }, [householdOn, activeWorkspace?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const planReady = !!plan && !plan.needs_plan;
  const t = planTerms(plan?.standard);
  const hhById = useMemo(() => {
    if (!planReady) return {};
    const out = {};
    for (const g of plan.groups) { out[g.id] = { ...g, groupColor: g.color }; for (const c of g.children) out[c.id] = { ...c, groupColor: g.color }; }
    return out;
  }, [plan, planReady]);
  const mappingOf = (catId) => (planReady ? plan.mapping.find((m) => m.category_id === catId) : null);

  const save = async () => {
    if (!dialog.name.trim()) return;
    setSaving(true);
    try {
      let id = dialog.id;
      if (id) await api.put(`/categories/${id}`, { name: dialog.name.trim(), color: dialog.color });
      else { const r = await api.post("/categories", { name: dialog.name.trim(), color: dialog.color }); id = r.data.id; }
      if (planReady && canWrite && (dialog.household_category_id || null) !== (dialog.initial_household_category_id || null)) {
        await api.put(`/household/mapping/${id}`, { household_category_id: dialog.household_category_id || null });
      }
      toast.success(dialog.id ? "Kategori oppdatert" : "Kategori opprettet");
      setDialog(null);
      load();
      loadPlan();
    } catch (e) { toast.error(errorMessage(e)); } finally { setSaving(false); }
  };

  const remove = async () => {
    try {
      await api.delete(`/categories/${del.id}`);
      toast.success("Kategori slettet");
      setDel(null);
      load();
      loadPlan();
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const openDialog = (c) => {
    const m = c ? mappingOf(c.id) : null;
    const hh = m?.household_category_id || "";
    setDialog(c ? { id: c.id, name: c.name, color: c.color, household_category_id: hh, initial_household_category_id: hh } : { name: "", color: CATEGORY_COLORS[0], household_category_id: "", initial_household_category_id: "" });
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Kategorier" description={`Grupper regningene dine. Nye fakturaer får automatisk foreslått kategori.${planReady ? ` Hver kategori kan kobles til en ${t.category} i ${t.regnskapOrd} – regninger uten kobling telles som «Ukategorisert».` : ""}`} actions={canWrite ? <Button onClick={() => openDialog(null)} data-testid="category-create-button"><Plus className="h-4 w-4 mr-2" /> Ny kategori</Button> : null} />

      {plan?.needs_plan ? (
        <Alert data-testid="categories-needs-plan">
          <BookOpenText className="h-4 w-4" />
          <AlertTitle>Regnskap venter på kontoplan</AlertTitle>
          <AlertDescription>Når virksomheten har valgt kontoplan, kan du koble hver Regninger-kategori til riktig kategori eller konto her. <Link to={HOUSEHOLD_HOME} className="underline font-medium" data-testid="categories-needs-plan-link">{plan.can_choose ? "Velg kontoplan" : "Åpne Regnskap"}</Link></AlertDescription>
        </Alert>
      ) : null}

      {!data ? <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-28" />)}</div> : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="categories-grid">
          {data.items.map((c) => {
            const m = mappingOf(c.id);
            const hh = m?.household_category_id ? hhById[m.household_category_id] : null;
            return (
              <Card key={c.id} className="card-lift" data-testid={`category-card-${c.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-2">
                    <Link to={`/fakturaer?category_id=${c.id}`} className="flex items-center gap-2.5 min-w-0 hover:underline">
                      <span className="h-3 w-3 rounded-full shrink-0" style={{ background: c.color }} />
                      <span className="font-semibold truncate">{c.name}</span>
                    </Link>
                    <div className="flex gap-1 shrink-0">
                      {canWrite ? <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openDialog(c)} aria-label="Rediger" data-testid={`category-edit-${c.id}`}><Pencil className="h-4 w-4" /></Button> : null}
                      {isOwner ? <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => setDel(c)} aria-label="Slett" data-testid={`category-delete-${c.id}`}><Trash2 className="h-4 w-4" /></Button> : null}
                    </div>
                  </div>
                  <div className="mt-4 flex items-end justify-between">
                    <div><p className="text-xs text-muted-foreground">Fakturaer</p><p className="font-medium tnum">{c.invoice_count}</p></div>
                    <div className="text-right"><p className="text-xs text-muted-foreground">Totalt</p><p className="font-medium tnum">{formatKr(c.total_amount)}</p></div>
                  </div>
                  {planReady ? (
                    <div className="mt-3 pt-3 border-t flex items-center gap-2 min-w-0" data-testid={`category-household-${c.id}`}>
                      <Link2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      {hh ? <CategoryChip code={hh.code} name={hh.name} color={hh.groupColor} testId={`category-household-chip-${c.id}`} /> : (
                        canWrite ? <button type="button" onClick={() => openDialog(c)} className="text-xs text-flag underline-offset-2 hover:underline text-left" data-testid={`category-household-chip-${c.id}`}>Ikke koblet – regninger telles som «Ukategorisert»</button>
                          : <span className="text-xs text-muted-foreground" data-testid={`category-household-chip-${c.id}`}>Ikke koblet til {t.regnskapOrd}</span>
                      )}
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            );
          })}
          <Card className="border-dashed">
            <CardContent className="p-5">
              <div className="flex items-center gap-2.5"><Tags className="h-4 w-4 text-muted-foreground" /><span className="font-semibold text-muted-foreground">Uten kategori</span></div>
              <div className="mt-4 flex items-end justify-between">
                <div><p className="text-xs text-muted-foreground">Fakturaer</p><p className="font-medium tnum">{data.uncategorized.count}</p></div>
                <div className="text-right"><p className="text-xs text-muted-foreground">Totalt</p><p className="font-medium tnum">{formatKr(data.uncategorized.total)}</p></div>
              </div>
              {data.uncategorized.count ? <Button variant="link" size="sm" className="px-0 mt-2" asChild><Link to="/fakturaer?category_id=ingen">Se fakturaer uten kategori</Link></Button> : null}
            </CardContent>
          </Card>
        </div>
      )}

      {planReady ? (
        <p className="text-xs text-muted-foreground" data-testid="categories-household-footer">
          <BookOpenText className="inline h-3.5 w-3.5 mr-1 align-text-bottom" />
          Koblingene styrer hvordan regninger føres i {t.regnskapOrd} ({t.planName}). Du kan også koble alle på én gang under <Link to={`${HOUSEHOLD_HOME}/kategorier#kobling`} className="underline" data-testid="categories-household-mapping-link">{t.isHousehold ? "Kategoriplan" : "Kontoplan"} → Kobling</Link>.
        </p>
      ) : null}

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="bg-card">
          <DialogHeader><DialogTitle>{dialog?.id ? "Rediger kategori" : "Ny kategori"}</DialogTitle><DialogDescription>Gi kategorien et navn og en farge{planReady ? `, og velg hvor regningene skal føres i ${t.regnskapOrd}` : ""}.</DialogDescription></DialogHeader>
          {dialog ? (
            <div className="space-y-4">
              <div className="space-y-1.5"><Label htmlFor="cname">Navn</Label><Input id="cname" value={dialog.name} onChange={(e) => setDialog({ ...dialog, name: e.target.value })} autoFocus data-testid="category-name-input" /></div>
              <div className="space-y-1.5">
                <Label>Farge</Label>
                <div className="flex flex-wrap gap-2">
                  {CATEGORY_COLORS.map((col) => (
                    <button key={col} type="button" onClick={() => setDialog({ ...dialog, color: col })} className={cn("h-8 w-8 rounded-full border-2", dialog.color === col ? "border-foreground" : "border-transparent")} style={{ background: col }} aria-label={`Farge ${col}`} data-testid={`category-color-${col.replace("#", "")}`} />
                  ))}
                </div>
              </div>
              {planReady ? (
                <div className="space-y-1.5">
                  <Label className="flex items-center gap-1.5"><BookOpenText className="h-4 w-4 text-primary" /> {t.isHousehold ? "Kategori i husholdningsregnskapet" : "Konto i kontoplanen"}</Label>
                  <CategoryPicker plan={plan} kind="utgift" value={dialog.household_category_id || null} onChange={(v) => setDialog({ ...dialog, household_category_id: v || "" })} noneLabel="Ikke koblet (telles som «Ukategorisert»)" placeholder={`Velg ${t.category}`} testId="category-household-picker" />
                  <p className="text-xs text-muted-foreground">Regninger i denne kategorien føres her i {t.regnskapOrd}, med mindre du overstyrer på den enkelte regningen.</p>
                </div>
              ) : null}
            </div>
          ) : null}
          <DialogFooter><Button variant="outline" onClick={() => setDialog(null)}>Avbryt</Button><Button onClick={save} disabled={saving || !dialog?.name?.trim()} data-testid="category-save-button">Lagre</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent className="bg-card">
          <AlertDialogHeader><AlertDialogTitle>Slette kategorien «{del?.name}»?</AlertDialogTitle><AlertDialogDescription>{del?.invoice_count ? `${del.invoice_count} fakturaer mister kategorien sin. ` : ""}Fakturaene slettes ikke.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Avbryt</AlertDialogCancel><AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={remove} data-testid="category-delete-confirm">Slett</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
